/**
 * Экспорт всех модулей курса
 *
 * Поддерживает lazy loading модулей по требованию.
 */

import type { CourseModule } from '@/types/lesson';

/** Карта загрузчиков модулей для lazy loading */
const moduleLoaders: Record<string, () => Promise<{ default: CourseModule }>> = {
  'module-1': () => import('./module-1').then((m) => ({ default: m.module1 })),
  'module-2': () => import('./module-2').then((m) => ({ default: m.module2 })),
  'module-3': () => import('./module-3').then((m) => ({ default: m.module3 })),
  'module-4': () => import('./module-4').then((m) => ({ default: m.module4 })),
  'module-5': () => import('./module-5').then((m) => ({ default: m.module5 })),
  'module-6': () => import('./module-6').then((m) => ({ default: m.module6 })),
};

/** Кэш загруженных модулей */
const moduleCache: Map<string, CourseModule> = new Map();

/** Мета-данные модулей для списка (без загрузки полного контента) */
const moduleMeta: Array<{
  id: string;
  slug: string;
  title: string;
  description: string;
  lessonCount: number;
}> = [
  {
    id: 'module-1',
    slug: 'basics',
    title: 'Основы Git',
    description: 'Базовые концепции и инициализация',
    lessonCount: 4,
  },
  {
    id: 'module-2',
    slug: 'basic-commands',
    title: 'Базовые команды Git',
    description: 'Добавление, коммиты, статус',
    lessonCount: 6,
  },
  {
    id: 'module-3',
    slug: 'branching',
    title: 'Ветвление и слияние',
    description: 'Работа с ветками',
    lessonCount: 5,
  },
  {
    id: 'module-4',
    slug: 'rewriting',
    title: 'Переписывание истории',
    description: 'Rebase, reset, amend',
    lessonCount: 5,
  },
  {
    id: 'module-5',
    slug: 'remote',
    title: 'Удалённые репозитории',
    description: 'Работа с remote',
    lessonCount: 5,
  },
  {
    id: 'module-6',
    slug: 'workflows',
    title: 'Продвинутые техники',
    description: 'Git Flow и best practices',
    lessonCount: 4,
  },
];

/**
 * Получить мета-данные всех модулей (без загрузки контента)
 */
export function getModuleMeta(): Array<{
  id: string;
  slug: string;
  title: string;
  description: string;
  lessonCount: number;
}> {
  return moduleMeta;
}

/**
 * Загрузить модуль по ID (lazy loading)
 */
export async function loadModule(moduleId: string): Promise<CourseModule | undefined> {
  // Проверяем кэш
  const cached = moduleCache.get(moduleId);
  if (cached) {
    return cached;
  }

  // Загружаем модуль
  const loader = moduleLoaders[moduleId];
  if (!loader) {
    console.error(`[Modules] Unknown module: ${moduleId}`);
    return undefined;
  }

  try {
    const loaded = await loader();
    moduleCache.set(moduleId, loaded.default);
    console.log(`[Modules] Loaded module: ${moduleId}`);
    return loaded.default;
  } catch (error) {
    console.error(`[Modules] Failed to load module ${moduleId}:`, error);
    return undefined;
  }
}

/**
 * Предзагрузить модуль (для оптимизации)
 */
export function preloadModule(moduleId: string): void {
  const loader = moduleLoaders[moduleId];
  if (loader && !moduleCache.has(moduleId)) {
    loader()
      .then((loaded) => {
        moduleCache.set(moduleId, loaded.default);
        console.log(`[Modules] Preloaded module: ${moduleId}`);
      })
      .catch((error) => {
        console.error(`[Modules] Failed to preload module ${moduleId}:`, error);
      });
  }
}

/**
 * Предзагрузить следующий модуль
 */
export function preloadNextModule(currentModuleId: string): void {
  const currentIndex = moduleMeta.findIndex((m) => m.id === currentModuleId);
  if (currentIndex >= 0 && currentIndex < moduleMeta.length - 1) {
    const nextModuleId = moduleMeta[currentIndex + 1].id;
    preloadModule(nextModuleId);
  }
}

/**
 * Получить все модули (async версия с lazy loading)
 */
export async function getModulesAsync(): Promise<CourseModule[]> {
  const modules: CourseModule[] = [];
  for (const meta of moduleMeta) {
    const module = await loadModule(meta.id);
    if (module) {
      modules.push(module);
    }
  }
  return modules;
}

/**
 * Получить модуль по ID (async версия)
 */
export async function getModuleAsync(moduleId: string): Promise<CourseModule | undefined> {
  return loadModule(moduleId);
}

/**
 * Получить модуль по slug (async версия)
 */
export async function getModuleBySlugAsync(slug: string): Promise<CourseModule | undefined> {
  const meta = moduleMeta.find((m) => m.slug === slug);
  if (meta) {
    return loadModule(meta.id);
  }
  return undefined;
}

// ============================================
// Синхронные экспорты для обратной совместимости
// ============================================

// Импорт модулей для синхронного доступа
import { module1 } from './module-1';
import { module2 } from './module-2';
import { module3 } from './module-3';
import { module4 } from './module-4';
import { module5 } from './module-5';
import { module6 } from './module-6';

// Реэкспорт модулей
export { module1, module2, module3, module4, module5, module6 };

/**
 * Все модули курса в порядке их следования (синхронная версия)
 * ВНИМАНИЕ: Загружает все модули сразу
 */
export const allModules: CourseModule[] = [module1, module2, module3, module4, module5, module6];

/**
 * Получить модуль по ID (синхронная версия)
 */
export function getModuleById(moduleId: string): CourseModule | undefined {
  return allModules.find((m) => m.id === moduleId);
}

/**
 * Получить модуль по slug (синхронная версия)
 */
export function getModuleBySlug(slug: string): CourseModule | undefined {
  return allModules.find((m) => m.slug === slug);
}
